/**
 * Initialisation i18next.
 *
 * ⚠️ **Une seule langue est soudée au chemin critique : le français.**
 *
 * Mesuré le 2026-08-11 : les deux dictionnaires importés en statique pesaient
 * 540 750 octets gzip — soit **plus de la moitié du chemin critique de CHAQUE
 * page** (990 962 o gz au total). L'anglais était du poids strictement mort pour
 * l'écrasante majorité des utilisateurs, qui ne le verront jamais.
 *
 * Pourquoi le français reste STATIQUE et lui seul devient dynamique — c'est
 * contre-intuitif : `fallbackLng` vaut `fr`, donc i18next charge le français de
 * toute façon, y compris pour un utilisateur anglophone. Rendre les deux
 * dynamiques n'économiserait donc pas un octet de plus, mais ajouterait un
 * aller-retour réseau AVANT le premier rendu — plus lent, pour rien.
 *
 * Reste à gagner (non fait) : découper le français par NAMESPACE pour ne charger
 * que les clés de la route affichée. Chantier à part entière.
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

// Langue de repli — la seule qui reste sur le chemin critique.
import translationFR from '@/locales/fr/translation.json'
import { estUneAdresseDeLEspace, langueChoisie } from '@/lib/langueChoisie'

/**
 * Langues chargées À LA DEMANDE. Chaque `import()` produit un chunk séparé, tiré
 * seulement quand la langue est réellement demandée (détection au démarrage ou
 * `i18n.changeLanguage()` depuis le sélecteur de langue / le profil utilisateur).
 */
const LAZY_LOCALES: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
    en: () => import('@/locales/en/translation.json'),
}

/**
 * D179 — **un nombre rond de millions ne rend jamais la clé brute.**
 *
 * `Intl.PluralRules('fr')` range 1 000 000, 2 000 000… dans la catégorie « many ». Le
 * catalogue français ne la porte presque jamais (mesuré le 2026-09-28 : 1 016 des 1 019
 * phrases au pluriel n'ont que `_one` / `_other`). i18next 23 cherche alors `<clé>_many`,
 * puis `<clé>` — jamais `_other` — et l'écran affiche « common.calendar.activeFilters ».
 *
 * Aucun réglage d'i18next ne corrige cela sans risque. `compatibilityJSON: 'v3'` quitte le
 * format `_one` / `_other` des 1 019 phrases. Un greffon `i18nFormat.addLookupKeys`
 * remplace toute la construction des clés (pluriel, contexte, `_zero`) et change le rendu de
 * `returnObjects`. `parseMissingKeyHandler` ne reçoit pas le nombre.
 *
 * Chaque catalogue reçoit donc, À SON CHARGEMENT et EN MÉMOIRE, `<base>_many` = `<base>_other`
 * quand `<base>_many` manque. Un `_many` déjà écrit reste tel quel. Les fichiers JSON ne
 * changent pas : les gardiens de parité, qui les lisent, n'exigent rien de neuf.
 *
 * ⛔ Le catalogue importé n'est JAMAIS modifié : seuls les objets qui reçoivent une forme, et
 * leurs parents, sont copiés. Un test qui importe le JSON ET ce module lit le fichier tel
 * qu'il est. Muter l'import rendrait aveugles les gardiens qui comptent les formes écrites.
 * L'anglais n'a pas de « many » : ses `_many` ne sont jamais lus, la règle vaut pour toute
 * langue chargée. Coût mesuré sur le catalogue français (28 625 textes), sous Node, sur la
 * machine de développement chargée : 3 à 15 ms au démarrage, contre 19 à 35 ms pour son
 * `JSON.parse`.
 *
 * ⛔ Tout catalogue ajouté en cours d'exécution doit passer par `avecLaFormeManyDesPluriels` :
 * une clé posée par `addResourceBundle`, `addResources` ou `addResource` n'a pas de `_many`
 * (clé brute à 1 000 000), un `_other` surchargé laisse `_many` sur l'ancien texte, et une
 * autre instance ne reçoit pas la règle. Aucun fichier de production hors de ce module n'ouvre
 * ces portes (gardien `i18n-d179-aucun-texte-n-entre-hors-du-chargement`). Hors de portée de
 * la règle : une clé ABSENTE du catalogue, dont i18next lit `defaultValue_many` puis
 * `defaultValue`, jamais `defaultValue_other` (`i18n-keys-exist` exige toute clé littérale
 * dans les deux catalogues) ; un `count` en chaîne, qui ne déclenche aucun pluriel (le type
 * d'i18next veut un nombre).
 */
export function avecLaFormeManyDesPluriels<T>(catalogue: T): T {
    if (typeof catalogue !== 'object' || catalogue === null || Array.isArray(catalogue)) {
        return catalogue
    }
    const source = catalogue as Record<string, unknown>
    let copie: Record<string, unknown> | null = null
    for (const cle of Object.keys(source)) {
        const valeur = source[cle]
        const complete = avecLaFormeManyDesPluriels(valeur)
        if (complete !== valeur) {
            copie ??= { ...source }
            copie[cle] = complete
        }
        if (!cle.endsWith('_other')) continue
        const many = `${cle.slice(0, -'_other'.length)}_many`
        if (!Object.prototype.hasOwnProperty.call(source, many)) {
            copie ??= { ...source }
            copie[many] = complete
        }
    }
    return (copie ?? catalogue) as T
}

/**
 * Backend i18next minimal (interface documentée du plugin, zéro dépendance).
 *
 * Le passer à i18next plutôt que d'appeler `addResourceBundle` à la main est ce
 * qui rend le changement INVISIBLE pour les 11 appelants de `changeLanguage()` :
 * i18next attend lui-même la fin du `read()` avant de résoudre la promesse de
 * `changeLanguage`, donc aucun écran ne s'affiche avec des clés brutes.
 */
const lazyLocaleBackend = {
    type: 'backend' as const,
    init: () => {},
    read(
        language: string,
        _namespace: string,
        callback: (err: unknown, data?: unknown) => void,
    ) {
        // `fr-FR`, `en-GB`… → la famille de langue.
        const loader = LAZY_LOCALES[language.split('-')[0]]
        if (!loader) {
            // Français (déjà dans `resources`) ou langue inconnue : rien à lire,
            // i18next retombe sur les ressources fournies puis sur `fallbackLng`.
            callback(null, {})
            return
        }
        loader().then(
            (mod) => callback(null, avecLaFormeManyDesPluriels(mod.default)),
            (err) => callback(err, false),
        )
    },
}

/**
 * A17 (décision PO du 2026-09-16) — **dans l'espace, la langue CHOISIE passe avant
 * le `?lang=` du lien.**
 *
 * Les e-mails de l'espace portent `?lang=` = la langue de la FICHE
 * (`services/portal/_portal_urls.py`). Lue en tête et remise en cache, elle remplaçait
 * la langue choisie dans « Mon compte », et l'effaçait pour la suite. Ce détecteur,
 * placé AVANT `querystring`, rend le choix du geste (`lib/langueChoisie.ts`).
 *
 * ⛔ Il ne rend RIEN hors de `/espace` : pour le back-office, la liste détectée est
 * celle d'avant, élément pour élément. ⛔ La détection n'écrit jamais un « choix » parce
 * que `caches` ne liste que `localStorage` (clé `i18nextLng`), jamais ce détecteur : l'y
 * ajouter ferait lever `changeLanguage` (il n'a pas de `cacheUserLanguage`), sans rien protéger.
 *
 * ⚠️ Un détecteur maison exige une INSTANCE : `init()` détecte de façon synchrone
 * (les ressources sont fournies), et un `addDetector` posé après lui arriverait trop
 * tard. i18next rappelle `init(services, detection)` sur cette instance, qui n'y réécrit
 * que les détecteurs intégrés (par leur nom) et garde le sien.
 */
const detecteurDeLangue = new LanguageDetector()
detecteurDeLangue.addDetector({
    name: 'choixDeLaPersonne',
    lookup: () =>
        typeof window !== 'undefined' && estUneAdresseDeLEspace(window.location.pathname)
            ? (langueChoisie() ?? undefined)
            : undefined,
})

/**
 * Promesse résolue quand la langue détectée est prête à être affichée.
 * `main.tsx` attend ce signal avant le premier rendu : sans lui, un utilisateur
 * anglophone verrait l'interface s'afficher en français puis basculer.
 * Pour un francophone elle est déjà tenue (le dictionnaire est bundlé).
 */
export const i18nReady = i18n
    // Détection de la langue du navigateur (et du choix de la personne, dans l'espace)
    .use(detecteurDeLangue)
    // Chargement à la demande des langues non bundlées
    .use(lazyLocaleBackend)
    // Initialisation react-i18next
    .use(initReactI18next)
    .init({
        resources: {
            fr: {
                // D179 : `_many` = `_other` en mémoire (voir `avecLaFormeManyDesPluriels`).
                translation: avecLaFormeManyDesPluriels(translationFR),
            },
        },
        // Sans ce drapeau, fournir `resources` désactive complètement le backend
        // et l'anglais ne serait JAMAIS chargé (interface muette en clés brutes).
        partialBundledLanguages: true,
        fallbackLng: 'fr', // Français par défaut
        // ⚠️ Jamais sous vitest (MODE `test`) : en debug, i18next imprime à chaque
        // chargement les catalogues FR et EN ENTIERS (≈ 2,2 Mo par fichier de test qui
        // importe ce module). Multiplié par les centaines de fichiers concernés, le
        // processus principal de vitest les garde en mémoire jusqu'au « heap out of
        // memory » : la CI s'arrêtait avant le premier rouge (mesuré le 2026-10-13).
        debug: import.meta.env.DEV && import.meta.env.MODE !== 'test',

        interpolation: {
            escapeValue: false, // React échappe déjà
        },

        detection: {
            // `choixDeLaPersonne` ne rend rien hors de `/espace` — voir plus haut.
            order: ['choixDeLaPersonne', 'querystring', 'localStorage', 'navigator', 'htmlTag'],
            lookupQuerystring: 'lang',
            caches: ['localStorage'],
        },
    })

/**
 * `<html lang>` suit la langue active — pour TOUS les appelants, pas pour un.
 *
 * ⚠️ Ce n'est pas un détail d'accessibilité : `localeActive()` (`lib/currency.ts`)
 * lit cet attribut, et c'est lui qui décide du formatage des **nombres**, des
 * **montants**, des **dates** et du **résumé de filtres** dans tout le socle
 * DataTable. Tant que l'attribut restait `fr` (valeur en dur d'`index.html`),
 * une session ouverte en anglais affichait des libellés anglais avec des
 * montants et des dates français — et rien à l'écran ne reliait les deux.
 *
 * L'attribut n'était posé que par `useLanguage()`, dont l'unique appelant est
 * la page Réglages › Apparence. Les six autres chemins de changement de langue
 * — connexion (`AuthContext`, 3 sites), fiche utilisateur, sélecteur des pages
 * publiques — appellent `changeLanguage()` sans jamais monter ce hook.
 *
 * L'écouteur vit donc ICI, au point que tous traversent : i18next émet
 * `languageChanged` après avoir chargé la locale, quel que soit l'appelant.
 */
i18n.on('languageChanged', (lng: string) => {
    if (typeof document !== 'undefined' && lng) {
        document.documentElement.lang = lng
    }
})

// Premier rendu : `languageChanged` n'a pas encore été émis si la langue
// détectée est déjà celle d'`index.html`. On synchronise une fois au démarrage.
if (typeof document !== 'undefined' && i18n.language) {
    document.documentElement.lang = i18n.language
}

export default i18n
